import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Check, X, Gauge, Sparkles, Wand2, Loader2 } from "lucide-react";

export type SeoIssueId =
  | "kw_in_title" | "kw_in_meta" | "kw_in_slug" | "kw_in_intro" | "kw_density"
  | "kw_in_subheadings" | "kw_in_image_alt" | "content_length"
  | "outbound_authority_link" | "internal_link"
  | "sentence_length" | "paragraph_length" | "has_lists" | "has_images" | "transition_words"
  | "title_length" | "meta_length" | "title_starts_with_keyword" | "power_words";

interface Props {
  keyword: string;
  title: string;
  h1: string;
  slug: string;
  metaDescription: string;
  bodyHtml: string;
  imagesAltCoverage: number;
  onFixIssue: (id: SeoIssueId) => void;
  onFixAll: () => void;
  onAutoLink: () => void;
  busyIssue?: SeoIssueId | "all" | "link" | null;
  fixAllProgress?: { current: number; total: number; label: string } | null;
}

function stripHtml(html: string): string {
  if (typeof window === "undefined") return html.replace(/<[^>]+>/g, " ");
  const d = document.createElement("div"); d.innerHTML = html; return d.textContent || "";
}

const TRANSITION_WORDS = ["d'abord","ensuite","puis","enfin","cependant","toutefois","néanmoins","par ailleurs","de plus","en outre","par conséquent","ainsi","donc","parce que","car","tandis que","alors que","également","notamment","en revanche","par exemple","en effet","finalement","surtout"];

const SeoScorePanel = ({ keyword, title, h1, slug, metaDescription, bodyHtml, imagesAltCoverage, onFixIssue, onFixAll, onAutoLink, busyIssue, fixAllProgress }: Props) => {
  const { groups, score, total } = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    const kwSlug = kw.replace(/\s+/g, "-");
    const text = stripHtml(bodyHtml).toLowerCase();
    const words = text.split(/\s+/).filter(Boolean);
    const wordCount = words.length;
    const intro = words.slice(0, Math.max(20, Math.floor(wordCount * 0.1))).join(" ");
    const occurrences = kw ? (text.match(new RegExp(kw.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g")) || []).length : 0;
    const density = kw && wordCount ? (occurrences * kw.split(/\s+/).length) / wordCount : 0;
    const headingsHtml = (bodyHtml.match(/<h[23][^>]*>[\s\S]*?<\/h[23]>/gi) || []).join(" ").toLowerCase();
    const altsHtml = (bodyHtml.match(/alt="([^"]*)"/gi) || []).join(" ").toLowerCase();
    const internalLinks = (bodyHtml.match(/href="\/(?!\/)/g) || []).length;
    const outboundAuthority = /href="https?:\/\/(?:[^"]*\.)?(wikipedia\.org|gov\.|\.gov|\.edu|unesco\.org|france\.fr|maroc\.ma|visitmorocco\.com)/i.test(bodyHtml);
    const sentences = stripHtml(bodyHtml).split(/[.!?]+/).filter((s) => s.trim().length > 5);
    const avgSentenceWords = sentences.length ? sentences.reduce((s, sn) => s + sn.split(/\s+/).length, 0) / sentences.length : 0;
    const paragraphs = (bodyHtml.match(/<p[^>]*>([\s\S]*?)<\/p>/gi) || []).map((p) => stripHtml(p).split(/\s+/).length);
    const maxPara = paragraphs.length ? Math.max(...paragraphs) : 0;
    const transitionCount = TRANSITION_WORDS.filter((w) => text.includes(w)).length;
    const hasLists = /<(ul|ol)/i.test(bodyHtml);
    const hasImages = /<img/i.test(bodyHtml);
    const tLen = title.length, mLen = metaDescription.length;
    const powerWords = ["meilleur","guide","2026","2025","top","ultime","complet","essentiel","comment","pourquoi"];
    const hasPower = powerWords.some((w) => title.toLowerCase().includes(w));

    const mk = (id: SeoIssueId, ok: boolean, label: string, weight: number, fixable = true) => ({ id, ok, label, weight, fixable });

    const basics = [
      mk("kw_in_title", !!kw && title.toLowerCase().includes(kw), "Mot-clé dans le titre SEO", 8),
      mk("kw_in_meta", !!kw && metaDescription.toLowerCase().includes(kw), "Mot-clé dans la meta description", 6),
      mk("kw_in_slug", !!kw && slug.toLowerCase().includes(kwSlug), "Mot-clé dans l'URL/slug", 6),
      mk("kw_in_intro", !!kw && intro.includes(kw), "Mot-clé dans les 10 % premiers", 6),
      mk("kw_density", density >= 0.012 && density <= 0.027, `Densité 1,2–2,7 % (actuel ${(density * 100).toFixed(2)} %)`, 8),
    ];
    const additional = [
      mk("kw_in_subheadings", !!kw && headingsHtml.includes(kw), "Mot-clé dans les sous-titres H2/H3", 5),
      mk("kw_in_image_alt", !!kw && altsHtml.includes(kw), "Mot-clé dans l'ALT d'une image", 4),
      mk("content_length", wordCount >= 1200, `Contenu ≥ 1 200 mots (actuel ${wordCount})`, 6),
      mk("outbound_authority_link", outboundAuthority, "Lien externe vers site d'autorité", 5),
      mk("internal_link", internalLinks >= 2, `Liens internes (${internalLinks})`, 6),
    ];
    const readability = [
      mk("sentence_length", avgSentenceWords > 0 && avgSentenceWords <= 22, `Phrases ≤ 22 mots (moy. ${avgSentenceWords.toFixed(0)})`, 4),
      mk("paragraph_length", maxPara <= 120, `Paragraphes ≤ 120 mots (max ${maxPara})`, 4),
      mk("has_lists", hasLists, "Listes à puces présentes", 3),
      mk("has_images", hasImages, "Images dans le contenu", 3),
      mk("transition_words", transitionCount >= 5, `Mots de transition (${transitionCount})`, 3),
    ];
    const meta = [
      mk("title_length", tLen >= 50 && tLen <= 60, `Titre 50–60 car. (${tLen})`, 6),
      mk("meta_length", mLen >= 120 && mLen <= 158, `Meta 120–158 car. (${mLen})`, 6),
      mk("title_starts_with_keyword", !!kw && title.toLowerCase().startsWith(kw), "Titre commence par le mot-clé", 3),
      mk("power_words", hasPower, "Power word dans le titre", 3),
    ];

    const groups = [
      { key: "basics", label: "Bases SEO", items: basics },
      { key: "additional", label: "Additionnels", items: additional },
      { key: "readability", label: "Lisibilité", items: readability },
      { key: "meta", label: "Titre & Meta", items: meta },
    ];
    const score = groups.reduce((s, g) => s + g.items.reduce((x, i) => x + (i.ok ? i.weight : 0), 0), 0);
    const total = groups.reduce((s, g) => s + g.items.reduce((x, i) => x + i.weight, 0), 0);
    const scaled = Math.round((score / total) * 100);
    return { groups, score: scaled, total };
  }, [keyword, title, h1, slug, metaDescription, bodyHtml, imagesAltCoverage]);

  const color = score >= 80 ? "text-emerald-600" : score >= 50 ? "text-amber-600" : "text-destructive";

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="font-heading text-base flex items-center gap-2">
          <Gauge className="h-4 w-4 text-primary" /> Score SEO (Rank Math Pro)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-baseline gap-3">
          <span className={`text-4xl font-heading font-bold ${color}`}>{score}</span>
          <span className="text-muted-foreground">/100</span>
        </div>
        <Progress value={score} className="h-2" />

        <div className="grid grid-cols-1 gap-2">
          <Button size="sm" onClick={onFixAll} disabled={busyIssue === "all"} className="bg-gradient-gold text-primary-foreground">
            {busyIssue === "all"
              ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              : <Wand2 className="h-4 w-4 mr-2" />}
            {busyIssue === "all"
              ? (fixAllProgress
                  ? `Correction ${fixAllProgress.current}/${fixAllProgress.total}…`
                  : "Correction en cours…")
              : "Tout corriger avec l'IA"}
          </Button>
          {busyIssue === "all" && fixAllProgress && (
            <div className="space-y-1">
              <Progress value={(fixAllProgress.current / fixAllProgress.total) * 100} className="h-1.5" />
              <p className="text-[11px] text-muted-foreground truncate">→ {fixAllProgress.label}</p>
            </div>
          )}
          <Button size="sm" variant="outline" onClick={onAutoLink} disabled={busyIssue === "link"}>
            {busyIssue === "link"
              ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              : <Sparkles className="h-4 w-4 mr-2" />}
            {busyIssue === "link" ? "Maillage en cours…" : "Générer maillage interne+externe (IA)"}
          </Button>
        </div>

        <Accordion type="multiple" defaultValue={["basics", "additional", "readability", "meta"]}>
          {groups.map((g) => {
            const failing = g.items.filter((i) => !i.ok).length;
            return (
              <AccordionItem key={g.key} value={g.key}>
                <AccordionTrigger className="text-sm py-2">
                  <span className="flex items-center gap-2">
                    {g.label}
                    {failing === 0
                      ? <span className="text-xs text-emerald-600">✓ Tout bon</span>
                      : <span className="text-xs text-destructive">{failing} à corriger</span>}
                  </span>
                </AccordionTrigger>
                <AccordionContent>
                  <ul className="space-y-1.5 text-xs">
                    {g.items.map((it) => (
                      <li key={it.id} className="flex items-start gap-2">
                        {it.ok
                          ? <Check className="h-3.5 w-3.5 text-emerald-600 mt-0.5 shrink-0" />
                          : <X className="h-3.5 w-3.5 text-destructive mt-0.5 shrink-0" />}
                        <span className={`flex-1 ${it.ok ? "text-foreground" : "text-muted-foreground"}`}>{it.label}</span>
                        {!it.ok && it.fixable && (
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => onFixIssue(it.id)}
                            disabled={busyIssue === it.id || busyIssue === "all"}
                            className="h-6 px-2 text-[11px]"
                          >
                            {busyIssue === it.id
                              ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                              : <Sparkles className="h-3 w-3 mr-1" />}
                            {busyIssue === it.id ? "Correction…" : "Corriger"}
                          </Button>
                        )}
                      </li>
                    ))}
                  </ul>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </CardContent>
    </Card>
  );
};

export default SeoScorePanel;